const ERROR_CODE_MESSAGES: Record<string, string> = {
  INSUFFICIENT_LIQUIDITY:
    'Not enough bridge liquidity for this route at the moment. Try a smaller amount or try again later.',
}

const looksTechnical = (message: string): boolean =>
  /[[{][\s\S]*"(code|path|expected|received|context|traceId)"\s*:/.test(message)

const extractNestedError = (message: string): { code?: string; message?: string } | null => {
  const start = message.search(/[[{]/)
  if (start === -1) return null
  try {
    const json = JSON.parse(message.slice(start))
    const inner = Array.isArray(json) ? json[0] : (json.errors?.[0] ?? json)
    if (!inner || typeof inner !== 'object') return null
    return {
      code: inner.context?.code ?? inner.code,
      message: inner.message,
    }
  } catch {
    return null
  }
}

const sanitizeIntentErrorMessage = (message: string, fallback: string): string => {
  const trimmed = message.trim()
  if (!trimmed) return fallback

  if (/^Invalid .+ quote response/i.test(trimmed)) {
    console.error('Provider returned invalid quote response:', message)
    return 'The quote provider returned an unexpected response.'
  }

  const nested = extractNestedError(trimmed)
  if (nested) {
    const mapped = nested.code ? ERROR_CODE_MESSAGES[nested.code] : undefined
    if (mapped) {
      console.error('Mapped provider error:', trimmed)
      return mapped
    }
    if (nested.message && !looksTechnical(nested.message)) {
      console.error('Nested provider error:', trimmed)
      return nested.message
    }
  }

  if (looksTechnical(trimmed)) {
    console.error('Unhandled technical error:', message)
    return fallback
  }

  return trimmed
}

export const parseIntentError = async (response: Response, fallback: string): Promise<Error> => {
  const text = await response.text()
  let rawMessage: string | undefined
  try {
    const json = JSON.parse(text)
    rawMessage = json.errors?.[0]?.message ?? json.message
  } catch {}
  if (!rawMessage && text.trim()) rawMessage = text
  if (!rawMessage) return new Error(fallback)
  return new Error(sanitizeIntentErrorMessage(rawMessage, fallback))
}
